import { _ } from '@ngx-translate/core';
import { of, take, filter, finalize, switchMap, Observable, throwError, catchError, BehaviorSubject } from 'rxjs';

import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';

import { AuthService } from '@/app/core/services/auth/auth.service';
import { AlertService } from '@/app/core/services/alert/alert.service';
import { AuthResponseInterface } from '@/app/core/services/auth/interfaces/auth-response.interfaces';

import { ResponseInterface } from '@/app/shared/interfaces/response.interface';

let isRefreshingToken = false;
const tokenSubject = new BehaviorSubject<string | null>(null);

export const AuthRefreshInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const alertService = inject(AlertService);

  const addToken = (req: HttpRequest<any>, token: string): HttpRequest<any> => {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  };

  const handleLogout = (): Observable<HttpEvent<any>> => {
    alertService.error(_('core.interceptors.auth-refresh.alert.session-time-out.error'));
    authService.clearCredential();
    router.navigate(['/auth/login']);

    return of({} as HttpEvent<any>);
  };

  const handle401Error = (
    nonAuthorizedRequest: HttpRequest<any>,
    nonAuthorizedNext: HttpHandlerFn,
  ): Observable<HttpEvent<any>> => {
    if (isRefreshingToken) {
      return tokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((jwt) => {
          return nonAuthorizedNext(addToken(nonAuthorizedRequest, jwt!));
        }),
      );
    } else {
      isRefreshingToken = true;
      tokenSubject.next(null);

      return authService.refreshToken().pipe(
        catchError(() => {
          // Return an observable that will be handled by the subsequent switchMap
          return of(null);
        }),
        switchMap((credentialsInfo: ResponseInterface<AuthResponseInterface> | null) => {
          if (credentialsInfo && credentialsInfo.payload?.content?.token?.key) {
            const token = credentialsInfo.payload.content.token.key;
            tokenSubject.next(token);
            return nonAuthorizedNext(addToken(nonAuthorizedRequest, token));
          }

          return handleLogout();
        }),
        finalize(() => {
          isRefreshingToken = false;
        }),
      );
    }
  };

  return next(request).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status === 401) {
        if (request.url.includes('auth/refresh')) {
          return throwError(() => error);
        }

        return handle401Error(request, next);
      }

      return throwError(() => error);
    }),
  );
};
